<script setup lang="ts">
	import '@vuepic/vue-datepicker/dist/main.css'
	import moment from 'moment'
	import 'moment/dist/locale/es'
	import { computed, reactive, ref, watch } from 'vue'
	import { Collapse } from 'vue-collapsed'
	import { useRoute } from 'vue-router'

	import DisclaimerModal from '@/components/DisclaimerModal.vue'
	import LoadingModal from '@/components/LoadingModal.vue'
	import ModalComponent from '@/components/ModalComponent.vue'
	import StyledPoliciesModal from '@/components/StyledPoliciesModal.vue'
	import PayProcessorIcon from '@/components/icons/PayProcessorIcon.vue'

	import useAuthUser from '@/composables/useAuthUser'
	import { useCurrencyFormat } from '@/composables/useCurrencyFormat'
	import useFixedNumber from '@/composables/useFixedNumber'

	import CancellationPolicy from '@/modules/hotels/components/CancellationPolicy.vue'
	import HotelInformation from '@/modules/hotels/components/HotelInformation.vue'
	import HotelRateGuestInformation from '@/modules/hotels/components/HotelRateGuestInformation.vue'
	import PolicyStatusBanner from '@/modules/hotels/components/PolicyStatusBanner.vue'
	import useHotelRate from '@/modules/hotels/composables/useHotelRate'
	import usePolicyEvaluator from '@/modules/hotels/composables/usePolicyEvaluator'
	import useUsers from '@/modules/users/composables/useUsers'

	import CountDownHotel from '../components/CountDownHotel.vue'
	import HotelRateComments from '../components/HotelRateComments.vue'
	import HotelRateImportantInformation from '../components/HotelRateImportantInformation.vue'
	import HotelRatePaymentMethod from '../components/HotelRatePaymentMethod.vue'
	import HotelRatePromotions from '../components/HotelRatePromotions.vue'
	import HotelRateSummary from '../components/HotelRateSummary.vue'
	import HotelRateTerms from '../components/HotelRateTerms.vue'
	import HotelRateTripReason from '../components/HotelRateTripReason.vue'
	import PolicyErrors from '../components/PolicyErrors.vue'

	const {
		form,
		route,
		travelers,
		invalidImage,
		imgUrl,
		hotelRate,
		isFetching,
		paymentOptions,
		selectedPaymentMethod,
		selectedCardId,
		companyCards,
		userCards,
		travelerCards,
		travelerId,
		disclaimerHandlers,
		disclaimerModalMessage,
		termsAndConditions,
		inputTypes,
		attemptedToSubmit,
		invoiceDisclaimer,
		creatingBooking,
		loadingMessage,
		company,
		credit,
		// companyGroup,
		// costCenter,
		guests,
		validationErrors,
		paymentTypes,
		policyInformation,
		validate,
		handleSubmit,
		setMandatoryCostCenterRequirement,
		policyObjectMapWithCountryNames,
		policyObjectMap,
		availablePolicies,
		loadingPolicies,
		actionShowModal,
		showModal,
		memberId,
		travelerEmail,
		cardUserId,
		travelerHasBillingInfo,
		tripReason,
		otherTripReason,
		tripReasonCatalog,
	} = useHotelRate()

	const { users } = useUsers()

	// Per-field dirty tracking — errors show on interaction, not just on submit
	const paymentMethodDirty = ref(false)
	const guestDirty = reactive<Record<string, boolean>>({})

	watch(selectedPaymentMethod, () => {
		paymentMethodDirty.value = true
	})
	watch(selectedCardId, () => {
		paymentMethodDirty.value = true
	})

	const markGuestDirty = (key: string) => {
		guestDirty[key] = true
	}
	const isGuestDirty = (key: string) =>
		guestDirty[key] || attemptedToSubmit.value

	const changeCurrency = ref(false)

	const serviceChargeDetails = computed(() => {
		const serviceChargeDetail = hotelRate.value?.total.serviceChargeDetails
		if (serviceChargeDetail && serviceChargeDetail.type === 'monto_fijo') {
			const amount = serviceChargeDetail.amount ?? '0'
			const currency = serviceChargeDetail.currency ?? ''

			return `${useCurrencyFormat(amount)} ${currency}`
		}
		return `${serviceChargeDetail?.percentage ?? '0'}%`
	})

	const subTotal = computed(() => {
		const total = hotelRate.value?.rates.reduce((acc, rate) => {
			return (
				acc +
				(changeCurrency.value ? rate.dailyFare : rate.approximateDailyFare)
			)
		}, 0)
		return useCurrencyFormat(total ?? 0)
	})

	const rateRoute = useRoute()

	const ratePolicyContext = computed(() => {
		// Noches y anticipación por FECHAS (End - Start), como HotelDetails — no por
		// rates.length: rates[] no garantiza una fila por noche (puede ser una tarifa
		// agregada o incluir filas de impuestos), y usar su length hacía que nightAmount
		// y dailyRateRange evaluaran sobre un valor equivocado (banner mentía).
		const start = rateRoute.query.Start as string | undefined
		const end = rateRoute.query.End as string | undefined

		const nights =
			start && end ?
				Math.max(
					1,
					Math.ceil(
						(new Date(end).getTime() - new Date(start).getTime()) / 86_400_000
					)
				)
			:	undefined

		const totalFare =
			hotelRate.value ?
				Number(hotelRate.value.total.approximateFare)
			:	undefined

		return {
			totalFare,
			nights,
			dailyRate:
				totalFare !== undefined && nights ? totalFare / nights : undefined,
			daysInAdvance:
				start ?
					Math.max(
						0,
						Math.ceil((new Date(start).getTime() - Date.now()) / 86_400_000)
					)
				:	undefined,
		}
	})

	const { evaluations: rateEvaluations, hardViolations: rateHardViolations } =
		usePolicyEvaluator(
			computed(() => policyObjectMap.value ?? null),
			ratePolicyContext
		)

	const rateBannerRef = ref<InstanceType<typeof PolicyStatusBanner> | null>(
		null
	)
	const ratePolicyAcceptanceComplete = ref(true)

	const onRateAcceptanceChange = (complete: boolean) => {
		ratePolicyAcceptanceComplete.value = complete
	}

	// W4: el banner vive tras v-if="rateEvaluations.length" — si las evaluaciones se
	// vacían (contexto cambió) se desmonta SIN emitir y el padre quedaría atorado en
	// false, bloqueando el submit sin razón. Sin evaluaciones no hay nada que aceptar.
	watch(rateEvaluations, (evaluations) => {
		if (evaluations.length === 0) {
			ratePolicyAcceptanceComplete.value = true
		}
	})

	const canSubmit = computed(
		() =>
			rateHardViolations.value.length === 0 &&
			ratePolicyAcceptanceComplete.value
	)
</script>

<template>
	<!-- Countdown Timer -->
	<div
		v-if="hotelRate?.expiresAt"
		class="relative z-10"
	>
		<CountDownHotel
			:visible="true"
			:expanded="true"
			:remaining="hotelRate?.expiresAt"
		/>
	</div>

	<div
		class="pb-8 lg:pb-8 bg-white min-h-screen"
		:class="{ 'pb-28': !isFetching && hotelRate && !loadingPolicies }"
	>
		<!-- Main Container -->
		<div
			v-motion
			:initial="{ opacity: 0 }"
			:enter="{
				opacity: 1,
				transition: {
					delay: 250,
				},
			}"
			v-if="!isFetching && hotelRate && !loadingPolicies"
			class="mx-auto w-full max-w-7xl px-3 pt-6"
		>
			<div class="w-full">
				<!-- Main content layout -->
				<div class="flex flex-col gap-4 lg:gap-8">
					<!-- Hotel Image Mobile - At the top -->
					<div
						v-if="!invalidImage"
						class="xl:hidden w-full h-48 sm:h-56 rounded-xl overflow-hidden"
					>
						<img
							class="w-full h-full object-cover"
							:src="imgUrl as string"
							alt="Hotel"
							@error="invalidImage = true"
						/>
					</div>

					<!-- Hotel details mobile -->
					<HotelInformation
						class-name="view-section-elegant xl:hidden"
						:rate="hotelRate"
					/>

					<!-- Main content grid -->
					<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-8">
						<!-- Left column - Forms and information -->
						<div class="lg:col-span-2 space-y-4 lg:space-y-8">
							<!-- Hotel details desktop -->
							<HotelInformation
								class-name="view-section-elegant hidden xl:block"
								:rate="hotelRate"
							/>

							<!-- Policies -->
							<StyledPoliciesModal
								v-if="availablePolicies"
								:policies="policyObjectMapWithCountryNames"
							>
								<template #button>
									<div
										class="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-all duration-300 cursor-pointer"
									>
										<div class="flex items-center gap-3">
											<div
												class="w-8 h-8 lg:w-10 lg:h-10 bg-[#021F58]/10 rounded-lg flex items-center justify-center"
											>
												<svg
													class="w-4 h-4 lg:w-5 lg:h-5 text-[#021F58]"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														stroke-linecap="round"
														stroke-linejoin="round"
														stroke-width="2"
														d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
													></path>
												</svg>
											</div>
											<div class="flex-1 min-w-0">
												<h3
													class="font-inter font-semibold text-sm lg:text-base text-gray-900 truncate"
												>
													Políticas del grupo
												</h3>
												<p class="text-xs lg:text-sm text-gray-600 truncate">
													{{ `${users[0].groupName} (${company?.name})` }}
												</p>
											</div>
											<svg
												class="w-4 h-4 lg:w-5 lg:h-5 text-gray-400 flex-shrink-0"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													stroke-linecap="round"
													stroke-linejoin="round"
													stroke-width="2"
													d="M9 5l7 7-7 7"
												></path>
											</svg>
										</div>
									</div>
								</template>
							</StyledPoliciesModal>

							<!-- Policy status banner -->
							<PolicyStatusBanner
								v-if="rateEvaluations.length"
								ref="rateBannerRef"
								:evaluations="rateEvaluations"
								variant="full"
								:show-compliant="true"
								@acceptance-change="onRateAcceptanceChange"
							/>

							<!-- Guest Information -->
							<HotelRateGuestInformation
								:hotel-code="hotelRate.hotelCode"
								:is-company-rate="hotelRate.isCompanyRateCode === true"
								:provider="hotelRate.provider ?? 'amadeus'"
								:guests="guests"
								:travelers="travelers"
								:form="form"
								:input-types="inputTypes"
								:validate="validate"
								:attempted-to-submit="attemptedToSubmit"
								:guest-dirty="guestDirty"
								:mark-guest-dirty="markGuestDirty"
								:is-guest-dirty="isGuestDirty"
								:users="users"
								:company="company"
								@submit="handleSubmit"
								@mandatory-change="setMandatoryCostCenterRequirement"
							/>

							<!-- Trip Reason -->
							<HotelRateTripReason
								v-model:reason="tripReason"
								v-model:other-reason="otherTripReason"
								:attempted-to-submit="attemptedToSubmit"
								:options="tripReasonCatalog.options.value"
								:required="tripReasonCatalog.required.value"
								:allow-other="tripReasonCatalog.allowOther.value"
								:is-fallback="tripReasonCatalog.isFallback.value"
								:loading="tripReasonCatalog.isLoading.value"
								:default-id="tripReasonCatalog.defaultId.value"
							/>

							<!-- Payment Method -->
							<HotelRatePaymentMethod
								v-model:selected-payment-method="selectedPaymentMethod"
								v-model:selected-card-id="selectedCardId"
								:payment-options="paymentOptions"
								:attempted-to-submit="attemptedToSubmit"
								:payment-method-dirty="paymentMethodDirty"
								:validation-message="validate('', inputTypes.paymentMethod)"
								:company-cards="companyCards"
								:user-cards="userCards"
								:traveler-cards="travelerCards"
								:credit="credit"
								:company="company!"
								:is-international="hotelRate.isInternational"
								:hotel-country-code="hotelRate.addresses[0]?.countryCode"
								:paymentMethod="hotelRate.paymentMethod"
								:payment-model="hotelRate.paymentModel"
								:member-id="memberId"
								:traveler-email="travelerEmail"
								:traveler-id="cardUserId"
								:traveler-user-id="travelerId"
								:traveler-has-billing-info="travelerHasBillingInfo"
							/>

							<!-- Hotelbeds contract remarks, visible before confirmation -->
							<HotelRateComments
								class="xl:hidden"
								:comments="hotelRate.rateComments"
							/>
							<HotelRatePromotions
								class="xl:hidden"
								:promotions="hotelRate.promotions"
							/>

							<CancellationPolicy
								class-name="bg-white rounded-xl border border-gray-200 p-4 lg:p-6 xl:hidden"
								:cancellation-policy="hotelRate.cancelPenalties"
								:has-restrictions="hotelRate.hasRestrictions"
								:payment-model="hotelRate.paymentModel"
							/>

							<!-- Policy Errors -->
							<PolicyErrors
								:policy-information="policyInformation"
								context="reservation"
							/>

							<!-- Important Information - Desktop Only -->
							<HotelRateImportantInformation class="hidden xl:block" />
						</div>

						<!-- Right column - Pricing and summary -->
						<div class="lg:col-span-1">
							<div class="sticky top-4 lg:top-8 space-y-4 lg:space-y-6">
								<!-- Hotel Image Desktop -->
								<div
									v-if="!invalidImage"
									class="hidden xl:block w-full h-56 rounded-xl overflow-hidden"
								>
									<img
										class="w-full h-full object-cover"
										:src="imgUrl as string"
										alt="Hotel"
										@error="invalidImage = true"
									/>
								</div>

								<HotelRateSummary
									:provider="hotelRate.provider"
									:change-currency="changeCurrency"
									:sub-total="subTotal"
									:service-charge-details="serviceChargeDetails"
									:hotel-rate="hotelRate"
									:start-date="$route.query.Start as string"
									:end-date="$route.query.End as string"
									:currency-code="hotelRate.total.currencyCode"
									:rates="hotelRate.rates"
									:total="hotelRate.total"
									:not-included-charges="hotelRate.notIncludedCharges"
									:can-submit="canSubmit"
								>
									<HotelRateComments
										class="hidden xl:block mx-4 mb-4 lg:mx-6"
										:comments="hotelRate.rateComments"
									/>
									<HotelRateTerms
										v-model:terms-and-conditions="termsAndConditions"
										:selected-payment-method="selectedPaymentMethod"
										:currency-code="hotelRate.total.currencyCode"
										:validation-errors="validationErrors"
									/>
								</HotelRateSummary>

								<HotelRatePromotions
									class="hidden xl:block"
									:promotions="hotelRate.promotions"
								/>

								<CancellationPolicy
									class-name="hidden xl:block bg-white rounded-xl border border-gray-200 p-4 lg:p-6"
									:cancellation-policy="hotelRate.cancelPenalties"
									:has-restrictions="hotelRate.hasRestrictions"
									:payment-model="hotelRate.paymentModel"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Important Information - Mobile Only (at the bottom) -->
			<HotelRateImportantInformation class="block xl:hidden" />
		</div>

		<!-- Modals -->
		<DisclaimerModal
			:message="disclaimerModalMessage"
			:is-open="invoiceDisclaimer.isOpen"
			:close="disclaimerHandlers.invoice.close"
		/>

		<ModalComponent :model-value="showModal">
			<template #modalBody>
				<div class="p-6">
					<div class="flex items-start gap-4">
						<div
							class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0"
						>
							<svg
								class="w-6 h-6 text-blue-600"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
								></path>
							</svg>
						</div>
						<div class="flex-1">
							<h3 class="font-inter font-semibold text-lg text-gray-900 mb-2">
								Agregar nueva tarjeta
							</h3>
							<p class="font-inter text-sm text-gray-600 mb-4">
								Para continuar con su reservación, necesitamos que agregue los
								datos de una tarjeta de pago. Esta información será procesada de
								forma segura.
							</p>
							<div class="flex gap-3">
								<button
									type="button"
									@click="actionShowModal"
									class="px-4 py-2 bg-blue-600 text-white font-inter text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
								>
									Entendido
								</button>
							</div>
						</div>
					</div>
				</div>
			</template>
		</ModalComponent>

		<LoadingModal
			:when="isFetching || creatingBooking || loadingPolicies"
			:message="loadingMessage"
		/>

		<!-- Fixed Mobile Reserve Button -->
		<div
			v-if="!isFetching && hotelRate && !loadingPolicies"
			class="fixed bottom-0 left-0 right-0 lg:hidden bg-white border-t border-gray-200 shadow-lg z-10"
		>
			<div class="px-4 py-3 safe-area-inset-bottom">
				<div class="flex items-center justify-between mb-2">
					<div>
						<p class="text-xs text-gray-600 font-inter">Total estancia</p>
						<p class="text-lg font-inter font-bold text-gray-900">
							${{
								changeCurrency ?
									`${useFixedNumber(hotelRate.total.fare).fixedNumber} ${hotelRate.total.currencyCode}`
								:	`${useFixedNumber(hotelRate.total.approximateFare).fixedNumber} ${hotelRate.total.requestedCurrency}`
							}}
						</p>
					</div>
					<button
						type="submit"
						form="reservation-form"
						:disabled="!!policyInformation?.errors?.length || !canSubmit"
						:class="[
							'px-6 py-3 rounded-xl font-inter font-semibold text-white transition-all duration-300 text-sm shadow-lg',
							policyInformation?.errors?.length || !canSubmit ?
								'bg-gray-400 cursor-not-allowed opacity-60'
							:	'bg-blue-600 hover:bg-blue-700 active:scale-95',
						]"
					>
						Reservar ahora
					</button>
				</div>
			</div>
		</div>
	</div>
</template>
